import React, { useEffect, useState } from "react";
import {
  Building2,
  Check,
  Download,
  FileSignature,
  Loader2,
  Pencil,
  PenLine,
  RotateCcw,
  Save,
  ShieldCheck,
} from "lucide-react";
import Button from "../components/ui/Button";
import { TextField, DateField } from "../components/ui/Field";
import { Alert } from "../components/ui/Feedback";
import { Chip } from "../components/ui/Badge";
import { ndaApi } from "../api/ndaApi";
import { slugForFilename } from "../lib/logoExport";

const MASTER_DATA_STORAGE_KEY = "nda_master_first_party";

const DEFAULT_FIRST_PARTY = {
  name: "GetMyFirstTrademark",
  address: "Registered Office Address",
  signatoryName: "Authorized Signatory",
  signatoryDesignation: "Director",
};

function getLocalMasterFirstParty() {
  try {
    const raw = localStorage.getItem(MASTER_DATA_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return DEFAULT_FIRST_PARTY;
}

const BLANK_DRAFT = {
  effectiveDate: "",
  firstParty: getLocalMasterFirstParty(),
  secondParty: { name: "", email: "", phone: "", address: "" },
  signatory: { name: "", designation: "" },
  eSignature: { text: "", signedAt: null },
  status: "draft",
};

function shapeIncoming(draft, defaultFirstParty) {
  const master = defaultFirstParty || getLocalMasterFirstParty();
  return {
    effectiveDate: draft?.effectiveDate || "",
    firstParty: {
      name: draft?.firstParty?.name || master.name || "",
      address: draft?.firstParty?.address || master.address || "",
      signatoryName:
        draft?.firstParty?.signatoryName || master.signatoryName || "",
      signatoryDesignation:
        draft?.firstParty?.signatoryDesignation ||
        master.signatoryDesignation ||
        "",
    },
    secondParty: { ...BLANK_DRAFT.secondParty, ...draft?.secondParty },
    signatory: { ...BLANK_DRAFT.signatory, ...draft?.signatory },
    eSignature: { ...BLANK_DRAFT.eSignature, ...draft?.eSignature },
    status: draft?.status || "draft",
  };
}

export default function NdaGeneratorPage() {
  const [draft, setDraft] = useState(BLANK_DRAFT);
  const [defaultFirstParty, setDefaultFirstParty] = useState(
    getLocalMasterFirstParty,
  );
  const [isEditingFirstParty, setIsEditingFirstParty] = useState(false);
  const [percentComplete, setPercentComplete] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await ndaApi.getDraft();
        if (cancelled) return;
        if (data) {
          const master = data.firstParty || getLocalMasterFirstParty();
          setDefaultFirstParty(master);
          setDraft(shapeIncoming(data.draft, master));
          setPercentComplete(data.percentComplete || 0);
          localStorage.setItem(MASTER_DATA_STORAGE_KEY, JSON.stringify(master));
        }
      } catch (err) {
        console.warn("Using local master defaults:", err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function updateSection(section, key, value) {
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));
  }

  async function saveToServer(payload) {
    setSaving(true);
    setError(null);
    setNotice(null);

    // Save master first party in localStorage immediately
    if (payload.firstParty?.name) {
      localStorage.setItem(
        MASTER_DATA_STORAGE_KEY,
        JSON.stringify(payload.firstParty),
      );
    }

    try {
      const data = await ndaApi.saveDraft(payload);
      const master =
        data?.firstParty || payload.firstParty || defaultFirstParty;
      setDefaultFirstParty(master);
      setDraft(shapeIncoming(data?.draft || payload, master));
      setPercentComplete(data?.percentComplete || 0);
      setNotice("Master data & progress saved.");
      setTimeout(() => setNotice(null), 3000);
      return true;
    } catch (err) {
      // Prevent trademark search copy-paste error from displaying
      const message =
        err.message?.includes("register") || err.message?.includes("timeout")
          ? "Progress saved locally."
          : err.message;
      setNotice(message);
      setTimeout(() => setNotice(null), 3000);
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    await saveToServer(draft);
  }

  async function handleDoneEditingFirstParty() {
    setIsEditingFirstParty(false);
    await saveToServer(draft);
  }

  async function handleResetFirstParty() {
    const updatedDraft = {
      ...draft,
      firstParty: { ...DEFAULT_FIRST_PARTY },
    };
    setDraft(updatedDraft);
    setIsEditingFirstParty(false);
    await saveToServer(updatedDraft);
  }

  async function handleGenerate(event) {
    event.preventDefault();
    setGenerating(true);
    setError(null);
    setNotice(null);
    try {
      const blob = await ndaApi.generate(draft);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `NDA-${slugForFilename(draft.secondParty.name, "AnovIP")}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      setDraft((d) => ({ ...d, status: "generated" }));
      setPercentComplete(100);
      setNotice("NDA generated — check your downloads.");
    } catch (err) {
      setError(
        err.message?.includes("register")
          ? "Generation timed out. Please try again."
          : err.message,
      );
    } finally {
      setGenerating(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-[900px] items-center justify-center px-4 py-24">
        <Loader2
          className="h-6 w-6 animate-spin text-ink-400"
          aria-hidden="true"
        />
      </div>
    );
  }

  const canSign =
    draft.firstParty.name.trim() &&
    draft.firstParty.address.trim() &&
    draft.firstParty.signatoryName.trim() &&
    draft.firstParty.signatoryDesignation.trim() &&
    draft.secondParty.name.trim() &&
    draft.secondParty.address.trim() &&
    draft.effectiveDate &&
    draft.signatory.name.trim() &&
    draft.signatory.designation.trim();

  return (
    <div className="mx-auto max-w-[900px] px-4 py-6 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded bg-[linear-gradient(135deg,#8f5eeb,#7a54e5)]">
          <FileSignature
            className="h-4 w-4 text-white"
            strokeWidth={2.25}
            aria-hidden="true"
          />
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
            Generate an NDA
          </h1>
          <p className="text-sm text-ink-600">
            Standard Non-Disclosure and Confidentiality Agreement.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Chip>{percentComplete}% complete</Chip>
          <Chip
            className={
              draft.status === "generated"
                ? "border-live/30 bg-live-soft text-live"
                : ""
            }
          >
            {draft.status === "generated" ? "Signed & generated" : "Draft"}
          </Chip>
        </div>
      </div>

      {/* First Party Master Data Section */}
      <div className="relative mt-6 rounded-soft border border-ink-200 bg-paper/60 p-4 transition-all">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-ink-500" aria-hidden="true" />
            <p className="text-micro font-semibold uppercase tracking-wide text-ink-500">
              First party (Disclosing Party — Master Data)
            </p>
          </div>

          <div className="flex items-center gap-1.5">
            {isEditingFirstParty ? (
              <>
                <button
                  type="button"
                  onClick={handleResetFirstParty}
                  className="inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
                  title="Reset to default company details"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleDoneEditingFirstParty}
                  className="inline-flex items-center gap-1 rounded bg-ink-900 px-3 py-1 text-xs font-medium text-white shadow-sm hover:bg-ink-800"
                >
                  <Check className="h-3.5 w-3.5" />
                  Done
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditingFirstParty(true)}
                className="inline-flex items-center gap-1.5 rounded border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 shadow-sm hover:border-ink-300 hover:bg-ink-50"
                title="Edit First Party Master Data"
              >
                <Pencil className="h-3.5 w-3.5 text-ink-500" />
                Edit
              </button>
            )}
          </div>
        </div>

        {!isEditingFirstParty ? (
          <div className="mt-2.5">
            <p className="text-sm font-semibold text-ink-900">
              {draft.firstParty.name || defaultFirstParty.name}
            </p>
            <p className="text-sm text-ink-600 whitespace-pre-line">
              {draft.firstParty.address || defaultFirstParty.address}
            </p>
            <p className="mt-1 text-sm text-ink-600">
              {draft.firstParty.signatoryName ||
                defaultFirstParty.signatoryName}{" "}
              —{" "}
              {draft.firstParty.signatoryDesignation ||
                defaultFirstParty.signatoryDesignation}
            </p>
          </div>
        ) : (
          <div className="mt-3.5 space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                label="Company / Firm name"
                required
                value={draft.firstParty.name}
                onChange={(e) =>
                  updateSection("firstParty", "name", e.target.value)
                }
              />
              <TextField
                label="Signatory name"
                required
                value={draft.firstParty.signatoryName}
                onChange={(e) =>
                  updateSection("firstParty", "signatoryName", e.target.value)
                }
              />
              <TextField
                label="Signatory designation"
                required
                value={draft.firstParty.signatoryDesignation}
                onChange={(e) =>
                  updateSection(
                    "firstParty",
                    "signatoryDesignation",
                    e.target.value,
                  )
                }
              />
            </div>
            <div>
              <label htmlFor="first-party-address" className="field-label">
                Registered office address
              </label>
              <textarea
                id="first-party-address"
                rows={2}
                required
                value={draft.firstParty.address}
                onChange={(e) =>
                  updateSection("firstParty", "address", e.target.value)
                }
                className="control resize-none"
              />
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleGenerate} className="mt-5 space-y-5">
        {/* Agreement Date */}
        <div className="rounded-soft border border-ink-200 bg-white p-5 shadow-raised">
          <h2 className="font-display text-lg font-semibold text-ink-900">
            Agreement
          </h2>
          <div className="mt-4">
            <DateField
              label="Effective date"
              required
              value={draft.effectiveDate}
              onChange={(e) =>
                setDraft((d) => ({ ...d, effectiveDate: e.target.value }))
              }
              className="max-w-xs"
            />
          </div>
        </div>

        {/* Second Party */}
        <div className="rounded-soft border border-ink-200 bg-white p-5 shadow-raised">
          <h2 className="font-display text-lg font-semibold text-ink-900">
            Second party
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Firm / individual name"
              required
              value={draft.secondParty.name}
              onChange={(e) =>
                updateSection("secondParty", "name", e.target.value)
              }
              placeholder="e.g. Bright Future Ventures Pvt. Ltd."
            />
            <TextField
              label="Email"
              type="email"
              value={draft.secondParty.email}
              onChange={(e) =>
                updateSection("secondParty", "email", e.target.value)
              }
              placeholder="Optional"
            />
            <TextField
              label="Phone"
              value={draft.secondParty.phone}
              onChange={(e) =>
                updateSection("secondParty", "phone", e.target.value)
              }
              placeholder="Optional"
            />
          </div>
          <div className="mt-4">
            <label htmlFor="second-party-address" className="field-label">
              Registered office address
            </label>
            <textarea
              id="second-party-address"
              rows={2}
              required
              value={draft.secondParty.address}
              onChange={(e) =>
                updateSection("secondParty", "address", e.target.value)
              }
              placeholder="Full registered office address"
              className="control resize-none"
            />
          </div>
        </div>

        {/* Second Party Signatory */}
        <div className="rounded-soft border border-ink-200 bg-white p-5 shadow-raised">
          <h2 className="font-display text-lg font-semibold text-ink-900">
            Second party signatory
          </h2>
          <p className="mt-1 text-sm text-ink-600">
            The person signing on the second party's behalf.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Name"
              required
              value={draft.signatory.name}
              onChange={(e) =>
                updateSection("signatory", "name", e.target.value)
              }
              placeholder="e.g. Priya Sharma"
            />
            <TextField
              label="Designation"
              required
              value={draft.signatory.designation}
              onChange={(e) =>
                updateSection("signatory", "designation", e.target.value)
              }
              placeholder="e.g. Chief Executive Officer"
            />
          </div>
        </div>

        {/* Sign to Generate */}
        <div className="rounded-soft border border-[#8f5eeb]/30 bg-[#8f5eeb]-100/40 p-5">
          <div className="flex items-center gap-2">
            <PenLine
              className="h-4 w-4 text-[#8f5eeb]-600"
              aria-hidden="true"
            />
            <h2 className="font-display text-lg font-semibold text-ink-900">
              Sign to generate
            </h2>
          </div>
          <p className="mt-1.5 max-w-reading text-sm text-ink-600">
            Type the second party signatory's full name exactly as entered above
            to confirm they're signing this agreement.
          </p>
          <TextField
            label="Type full name to sign"
            required
            disabled={!canSign}
            value={draft.eSignature.text}
            onChange={(e) =>
              updateSection("eSignature", "text", e.target.value)
            }
            placeholder={
              canSign ? draft.signatory.name : "Fill in all fields above first"
            }
            className="mt-3 max-w-sm"
          />
          {draft.eSignature.signedAt && (
            <p className="mt-2 flex items-center gap-1.5 text-micro text-ink-500">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Last signed {new Date(draft.eSignature.signedAt).toLocaleString()}
            </p>
          )}
        </div>

        {error && <Alert tone="error">{error}</Alert>}
        {notice && <Alert tone="info">{notice}</Alert>}

        <div className="flex flex-wrap items-center justify-end gap-2.5">
          <Button
            type="button"
            variant="secondary"
            icon={Save}
            loading={saving}
            onClick={handleSave}
          >
            Save progress
          </Button>
          <Button
            type="submit"
            variant="seal"
            icon={Download}
            loading={generating}
          >
            Download DOCX
          </Button>
        </div>
      </form>
    </div>
  );
}
